import { And, Asc, Eq, Relation, Or, Expression } from '@cotera/era';
import { EventStreamDefinition } from '../../manifest';
import _ from 'lodash';
import { SYSTEM_TABLES } from '../../runtime/system-tables/system-tables';
import { streamSpecifierToEventStreamIds } from '../../builder/stream-specifier-to-event-stream-ids';

export type UnprocessedEntityEventsCursor = {
  readonly coteraStableId: string;
  readonly detectedAt: Date;
};

export const UnprocessedEntityEventsForSink = (params: {
  readonly stable_ids?: Relation;
  readonly entity_events?: Relation;
  readonly eventStream: Pick<
    EventStreamDefinition,
    'rel' | 'sinks' | 'identifier' | 'specifier'
  >;
  readonly sink: { readonly sinkId: string };
  readonly cursor: UnprocessedEntityEventsCursor | null;
}): Relation => {
  const {
    stable_ids = SYSTEM_TABLES.stable_ids,
    entity_events = SYSTEM_TABLES.entity_events,
    cursor,
    eventStream: resource,
  } = params;
  const { entityName, streamId } = streamSpecifierToEventStreamIds(
    resource.specifier
  );

  const rel = Relation.fromAst(resource.rel);

  const sink = resource.sinks[params.sink.sinkId];

  if (sink === undefined) {
    throw new Error(
      `Sink "${JSON.stringify(params.sink)}" not found in entity`
    );
  }

  const TargetWithSid = rel
    .select((t) => ({
      ...t.star(),
      __identifier: Expression.fromAst(resource.identifier).cast('string'),
    }))
    .innerJoin(
      stable_ids.where((t) => t.attr('definition_id').eq(entityName)),
      (target, sids) => ({
        on: Eq(sids.attr('identifier'), target.attr('__identifier')),
        select: { ...target.star(), ...sids.pick('cotera_stable_id') },
      })
    );

  return entity_events
    .select((t) => t.star(), {
      condition: (t) =>
        And(
          t.attr('entity_id').eq(entityName),
          t.attr('event_stream_id').eq(streamId)
        ),
    })
    .andThen((rel) =>
      cursor === null
        ? rel
        : rel.where((t) =>
            Or(
              t.attr('detected_at_time').gt(cursor.detectedAt),
              And(
                t.attr('detected_at_time').eq(cursor.detectedAt),
                t.attr('cotera_stable_id').gt(cursor.coteraStableId)
              )
            )
          )
    )
    .leftJoin(TargetWithSid, (event, target) => ({
      on: Eq(target.attr('cotera_stable_id'), event.attr('cotera_stable_id')),
      select: {
        ...target.star(),
        __event_cotera_stable_id: event.attr('cotera_stable_id'),
        __event_value: event.attr('value'),
        __event_detected_at: event.attr('detected_at_time'),
        __target_deleted: target.attr('cotera_stable_id').isNull(),
      },
    }))
    .select((t) => ({
      ..._.mapValues(sink.select, (expr) => Expression.fromAst(expr)),
      ...t.pick(
        '__event_cotera_stable_id',
        '__event_value',
        '__event_detected_at',
        '__target_deleted'
      ),
      __event_meets_sink_condition: Expression.fromAst(sink.condition),
    }))
    .orderBy((t) => [
      Asc(t.attr('__event_detected_at')),
      Asc(t.attr('__event_cotera_stable_id')),
    ]);
};
